import { useState, useEffect, useMemo, useRef } from 'react';
import { formatDateNL } from '../utils/dateFormat';
import { axiosInstance } from '../App';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Plus, Trash2, Receipt, Edit, X, Search, FileText, DollarSign, Filter, Sparkles, Loader2, ArrowRightLeft } from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { BoekhouderExportButton } from './BoekhouderExport';
import { CsvImportButton, DuplicatesButton, BulkScanDialog } from './ExpenseImportTools';

const fmtEuro = (v) => `€ ${(Number(v) || 0).toLocaleString('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const CATEGORY_LABELS = {
  parkeren: '🅿️ Parkeren',
  material: '🧱 Materiaal',
  transport: '🚚 Transport',
  equipment: '🔧 Gereedschap',
  services: '👷 Onderaannemers & diensten',
  subscriptions: '💻 Software & abonnementen',
  insurance: '🛡️ Verzekering & lidmaatschap',
  office: '🗂️ Kantoor & marketing',
  other: '📌 Overig',
};

const emptyForm = { client_name: '', address: '', description: '', amount: '', date: new Date().toISOString().slice(0,10), category: 'parkeren' };

const Expenses = ({ user }) => {
  const { t } = useTranslation();
  const readOnly = user?.role === 'boekhouder';
  const [expenses, setExpenses] = useState([]);
  const [clients, setClients] = useState([]);
  const [users, setUsers] = useState([]);
  const [totalAmount, setTotalAmount] = useState(0);
  const [byCategory, setByCategory] = useState({});
  const [totalCount, setTotalCount] = useState(0);

  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [selectedReceipt, setSelectedReceipt] = useState(null);
  const [selectedClientId, setSelectedClientId] = useState('');
  const [formData, setFormData] = useState({ ...emptyForm });

  // Filters
  const [filters, setFilters] = useState({
    search: '',
    client_name: 'all',
    category: 'all',
    user_id: 'all',
    start_date: '',
    end_date: '',
  });

  const bulkInputRef = useRef(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulk, setBulk] = useState(null); // {files:[{name,size,status,result,error}], done, running}
  const handleBulkScan = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length) return;
    setBulkBusy(true);
    const state = { files: files.map((f) => ({ name: f.name, size: f.size, status: 'wachten' })), done: 0, running: true, startedAt: Date.now() };
    setBulk({ ...state });
    for (let i = 0; i < files.length; i++) {
      state.files[i].status = 'bezig'; setBulk({ ...state, files: [...state.files] });
      const fd = new FormData(); fd.append('files', files[i]);
      try {
        const r = await axiosInstance.post('/expenses/bulk-scan', fd, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 180000 });
        const created = r.data.created?.[0]; const failed = r.data.failed?.[0];
        if (created) { state.files[i] = { ...state.files[i], status: 'ok', result: created }; }
        else { state.files[i] = { ...state.files[i], status: failed?.duplicate ? 'dubbel' : 'mislukt', error: failed?.error || 'AI kon de bon niet lezen' }; }
      } catch (err) {
        state.files[i] = { ...state.files[i], status: 'mislukt', error: err.response?.data?.detail || err.message || 'Verwerken mislukt' };
      }
      state.done = i + 1; setBulk({ ...state, files: [...state.files] });
    }
    state.running = false; setBulk({ ...state, files: [...state.files] });
    setBulkBusy(false);
    const ok = state.files.filter((f) => f.status === 'ok').length;
    toast.success(`${ok} van ${files.length} bon(nen) toegevoegd${files.length - ok ? ` · ${files.length - ok} niet verwerkt (zie overzicht)` : ''}`, { duration: 6000 });
    fetchExpenses();
  };

  const btwSplit = (exp) => {
    const ex = exp.import_extracted || {};
    const incl = Number(ex.total_incl_btw ?? exp.amount ?? 0);
    const rate = Number(exp.btw_percentage ?? 21);
    const excl = ex.total_excl_btw != null ? Number(ex.total_excl_btw) : Math.round((incl / (1 + rate / 100)) * 100) / 100;
    const btw = ex.btw_amount != null ? Number(ex.btw_amount) : Math.round((incl - excl) * 100) / 100;
    return { incl, excl, btw, rate };
  };

  const changeBtw = async (exp, rate) => {
    try {
      const r = await axiosInstance.patch(`/expenses/${exp.id}/btw`, { btw_percentage: rate });
      toast.success(`Btw op ${rate}% gezet: ${fmtEuro(r.data.excl)} excl. + ${fmtEuro(r.data.btw)} btw = ${fmtEuro(r.data.incl)}`);
      fetchExpenses();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Btw aanpassen mislukt');
    }
  };

  const [editingAmountId, setEditingAmountId] = useState(null);
  const [amountDraft, setAmountDraft] = useState('');
  const startAmountEdit = (exp) => { setEditingAmountId(exp.id); setAmountDraft(String(btwSplit(exp).incl)); };
  const saveAmount = async (exp) => {
    const val = parseFloat(String(amountDraft).replace(',', '.'));
    setEditingAmountId(null);
    if (!val || val <= 0 || Math.abs(val - btwSplit(exp).incl) < 0.005) return;
    try {
      const r = await axiosInstance.patch(`/expenses/${exp.id}/amount`, { amount: val });
      toast.success(`Bedrag aangepast: ${fmtEuro(r.data.excl)} excl. + ${fmtEuro(r.data.btw)} btw = ${fmtEuro(r.data.incl)}`);
      fetchExpenses();
    } catch (e) { toast.error(e.response?.data?.detail || 'Bedrag aanpassen mislukt'); }
  };

  const openReceipt = async (exp) => {
    const win = window.open('', '_blank');
    try {
      const r = await axiosInstance.get(`/expenses/${exp.id}/receipt-url`);
      if (win) win.location.href = r.data.url; else window.open(r.data.url, '_blank');
    } catch (e) {
      if (win) win.close();
      toast.error(e.response?.data?.detail || 'Bon openen mislukt');
    }
  };

  const changeCategory = async (exp, category) => {
    if (category === exp.category) return;
    try {
      const r = await axiosInstance.patch(`/expenses/${exp.id}/category`, { category, apply_to_vendor: false });
      const vendor = r.data.learned_vendor;
      let msg = `Categorie aangepast naar ${CATEGORY_LABELS[category]}`;
      if (vendor) msg += ` · AI onthoudt dit voor "${vendor}"`;
      if (r.data.other_vendor_count > 0 && window.confirm(`Ook ${r.data.other_vendor_count} andere uitgave(n) van "${vendor || exp.client_name || exp.project_name}" op ${CATEGORY_LABELS[category]} zetten?`)) {
        const r2 = await axiosInstance.patch(`/expenses/${exp.id}/category`, { category, apply_to_vendor: true });
        msg += ` · ${r2.data.applied_to_vendor} andere uitgave(n) bijgewerkt`;
      }
      toast.success(msg, { duration: 6000 });
      fetchExpenses();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Categorie aanpassen mislukt');
    }
  };

  const fetchExpenses = async () => {
    try {
      const params = { limit: 500 };
      if (filters.client_name !== 'all') params.client_name = filters.client_name;
      if (filters.category !== 'all') params.category = filters.category;
      if (filters.user_id !== 'all' && user?.is_admin) params.user_id = filters.user_id;
      if (filters.start_date) params.start_date = filters.start_date;
      if (filters.end_date) params.end_date = filters.end_date;
      if (filters.search) params.search = filters.search;
      const r = await axiosInstance.get('/expenses', { params });
      setExpenses(r.data.expenses || []);
      setTotalAmount(r.data.total_amount || 0);
      setByCategory(r.data.by_category || {});
      setTotalCount(r.data.total || 0);
    } catch { toast.error('Erro ao carregar despesas'); }
  };

  const fetchClients = async () => {
    try { const r = await axiosInstance.get('/clients/all'); setClients(r.data); } catch {}
  };
  const fetchUsers = async () => {
    if (!user?.is_admin) return;
    try { const r = await axiosInstance.get('/users'); setUsers(r.data); } catch {}
  };

  useEffect(() => { fetchClients(); fetchUsers(); }, []);
  useEffect(() => {
    const t = setTimeout(fetchExpenses, 250);
    return () => clearTimeout(t);
  }, [filters.search, filters.client_name, filters.category, filters.user_id, filters.start_date, filters.end_date]);

  const clearFilters = () => setFilters({ search: '', client_name: 'all', category: 'all', user_id: 'all', start_date: '', end_date: '' });

  const handleSubmit = async (e) => {
    e.preventDefault();
    const selectedClient = clients.find(c => c.id === selectedClientId);
    if (selectedClient && selectedClient.addresses && selectedClient.addresses.length > 0 && !formData.address) {
      toast.error('Selecione um endereço para este cliente');
      return;
    }
    try {
      const submitData = new FormData();
      submitData.append('client_name', formData.client_name);
      if (formData.address) submitData.append('address', formData.address);
      submitData.append('description', formData.description);
      submitData.append('amount', formData.amount);
      submitData.append('date', formData.date);
      submitData.append('category', formData.category);
      if (selectedReceipt) submitData.append('receipt', selectedReceipt);

      if (editingId) {
        await axiosInstance.put(`/expenses/${editingId}`, submitData, { headers: { 'Content-Type': 'multipart/form-data' } });
        toast.success('Despesa atualizada!');
      } else {
        await axiosInstance.post('/expenses', submitData, { headers: { 'Content-Type': 'multipart/form-data' } });
        toast.success('Despesa adicionada!');
      }
      resetForm();
      fetchExpenses();
    } catch {
      toast.error(editingId ? 'Erro ao atualizar despesa' : 'Erro ao adicionar despesa');
    }
  };

  const resetForm = () => {
    setOpen(false); setEditingId(null); setSelectedReceipt(null); setSelectedClientId('');
    setFormData({ ...emptyForm });
  };

  const handleEdit = (exp) => {
    setEditingId(exp.id);
    const client = clients.find(c => c.name === exp.client_name);
    if (client) setSelectedClientId(client.id);
    setFormData({
      client_name: exp.client_name || '',
      address: exp.address || '',
      description: exp.description || '',
      amount: exp.amount || '',
      date: exp.date || '',
      category: exp.category || 'parkeren',
    });
    setOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Deze uitgave verwijderen?')) return;
    try { await axiosInstance.delete(`/expenses/${id}`); toast.success('Verwijderd'); fetchExpenses(); }
    catch { toast.error('Fout bij verwijderen'); }
  };

  const MOVE_TARGETS = {
    purchase: { label: 'Inkoopfacturen', endpoint: 'convert-to-purchase-invoice' },
    external: { label: 'Facturen van derden', endpoint: 'convert-to-external-invoice' },
  };
  const [selected, setSelected] = useState(new Set());
  const [bulkMoving, setBulkMoving] = useState(false);
  const toggleSelect = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const allSelected = expenses.length > 0 && expenses.every((e) => selected.has(e.id));
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(expenses.map((e) => e.id)));
  const handleBulkMove = async (target) => {
    const t = MOVE_TARGETS[target];
    if (!t || selected.size === 0) return;
    const ids = expenses.filter((e) => selected.has(e.id)).map((e) => e.id);
    const total = expenses.filter((e) => selected.has(e.id)).reduce((a, e) => a + Number(e.amount || 0), 0);
    if (!window.confirm(`${ids.length} uitgave(n) (${fmtEuro(total)}) verplaatsen naar ${t.label}?`)) return;
    setBulkMoving(true);
    try {
      const r = await axiosInstance.post('/expenses/bulk-move', { ids, target });
      toast[r.data.failed_count ? 'warning' : 'success'](r.data.message + (r.data.failed?.length ? ` — ${r.data.failed.map((f) => `${f.description || f.id}: ${f.error}`).join('; ')}` : ''), { duration: 8000 });
      setSelected(new Set());
      fetchExpenses();
    } catch (e) { toast.error(e.response?.data?.detail || 'Verplaatsen mislukt'); }
    finally { setBulkMoving(false); }
  };
  const handleMove = async (exp, target) => {
    const t = MOVE_TARGETS[target];
    if (!t) return;
    if (!window.confirm(`"${exp.description}" (${fmtEuro(exp.amount)}) verplaatsen naar ${t.label}?`)) return;
    try {
      const r = await axiosInstance.post(`/expenses/${exp.id}/${t.endpoint}`);
      toast.success(r.data.message || `Verplaatst naar ${t.label}`);
      fetchExpenses();
    } catch (e) { toast.error(e.response?.data?.detail || 'Verplaatsen mislukt'); }
  };

  const [scanning, setScanning] = useState(false);
  const scanInputRef = useRef(null);

  const handleReceiptScan = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setScanning(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await axiosInstance.post('/expenses/parse-receipt', fd, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 90000 });
      const x = r.data.extracted || {};
      setEditingId(null);
      setSelectedClientId('');
      setFormData({
        ...emptyForm,
        description: [x.vendor, x.description].filter(Boolean).join(' — ') || 'Bon',
        amount: x.amount != null ? String(x.amount) : '',
        date: x.date || new Date().toISOString().slice(0, 10),
        category: x.category || 'other',
      });
      setSelectedReceipt(file);
      setOpen(true);
      toast.success('Bon gelezen — controleer de gegevens en sla op');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Bon lezen mislukt — probeer een scherpere foto');
    } finally {
      setScanning(false);
    }
  };

  // Distinct client names for filter (from current loaded clients)
  const clientOptions = useMemo(() => clients.map(c => c.name).sort(), [clients]);

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-4xl font-bold text-gray-800">Uitgaven</h1>
          <p className="text-sm text-gray-500 mt-1">Bonnen materiaal, parkeren en andere kosten</p>
        </div>
        <div className="flex gap-2">
          {readOnly ? (<>
            <span className="text-xs px-3 py-1.5 rounded-full bg-[#1a5276]/10 text-[#1a5276] font-medium self-center" data-testid="readonly-badge">Alleen lezen — bekijken en downloaden</span>
            <BoekhouderExportButton readOnly />
          </>) : (<>
          <input ref={scanInputRef} type="file" accept="image/*,application/pdf" className="hidden" onChange={handleReceiptScan} data-testid="receipt-scan-input" />
          <Button variant="outline" onClick={() => scanInputRef.current?.click()} disabled={scanning} className="border-[#3da5a5] text-[#2d8585] hover:bg-[#3da5a5]/10" data-testid="receipt-scan-btn">
            {scanning ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />} {scanning ? 'AI leest bon...' : 'Bon scannen (AI)'}
          </Button>
          <BulkScanDialog state={bulk} onClose={() => setBulk(null)} />
          {user?.is_admin && <BoekhouderExportButton />}
          {user?.is_admin && <DuplicatesButton onChanged={fetchExpenses} />}
          <CsvImportButton categories={CATEGORY_LABELS} onDone={fetchExpenses} />
          <input ref={bulkInputRef} type="file" multiple accept="image/*,application/pdf" className="hidden" onChange={handleBulkScan} data-testid="receipt-bulk-input" />
          <Button variant="outline" onClick={() => bulkInputRef.current?.click()} disabled={bulkBusy} className="border-[#1a5276] text-[#1a5276] hover:bg-[#1a5276]/5" data-testid="receipt-bulk-btn" title="Selecteer meerdere bonnen: AI leest ze en maakt per bon een uitgave aan met de bon als bijlage">
            {bulkBusy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Receipt className="w-4 h-4 mr-2" />} {bulkBusy ? 'Bonnen verwerken...' : 'Meerdere bonnen uploaden'}
          </Button>
          <Button onClick={() => { setEditingId(null); setFormData({ ...emptyForm }); setSelectedClientId(''); setSelectedReceipt(null); setOpen(true); }} className="bg-[#3da5a5] hover:bg-[#2d8585]" data-testid="new-expense-btn">
            <Plus className="w-4 h-4 mr-2" /> Nieuwe Uitgave
          </Button>
          </>)}
        </div>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-[#3da5a5] to-[#2d8585] border-0">
          <CardContent className="py-5">
            <div className="flex items-center gap-2 text-teal-100 text-sm"><DollarSign className="w-4 h-4" />Totaal</div>
            <p className="text-3xl font-bold text-white" data-testid="total-amount">{fmtEuro(totalAmount)}</p>
            <p className="text-teal-100 text-xs">{totalCount} bonnen · waarvan btw <span className="font-semibold text-white" data-testid="total-btw">{fmtEuro(expenses.reduce((a, e) => a + btwSplit(e).btw, 0))}</span></p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-blue-500 to-blue-600 border-0">
          <CardContent className="py-5">
            <div className="flex items-center gap-2 text-blue-100 text-sm">🅿️ Parkeren</div>
            <p className="text-3xl font-bold text-white">{fmtEuro(byCategory.parkeren?.total || 0)}</p>
            <p className="text-blue-100 text-xs">{byCategory.parkeren?.count || 0} bonnen</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-orange-500 to-orange-600 border-0">
          <CardContent className="py-5">
            <div className="flex items-center gap-2 text-orange-100 text-sm">🧱 Materiaal</div>
            <p className="text-3xl font-bold text-white">{fmtEuro(byCategory.material?.total || 0)}</p>
            <p className="text-orange-100 text-xs">{byCategory.material?.count || 0} bonnen</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-purple-500 to-purple-600 border-0">
          <CardContent className="py-5">
            <div className="flex items-center gap-2 text-purple-100 text-sm">📌 Overig</div>
            <p className="text-3xl font-bold text-white">{fmtEuro(Math.max(0, totalAmount - (byCategory.parkeren?.total || 0) - (byCategory.material?.total || 0)))}</p>
            <p className="text-purple-100 text-xs">{Math.max(0, totalCount - (byCategory.parkeren?.count || 0) - (byCategory.material?.count || 0))} bonnen</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="bg-white shadow-sm border-gray-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-gray-800 text-lg flex items-center gap-2"><Filter className="w-5 h-5" />Filters</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-3">
            <div className="lg:col-span-2">
              <Label className="text-gray-700 text-xs">Zoeken</Label>
              <div className="relative">
                <Search className="w-4 h-4 absolute left-2 top-3 text-gray-400" />
                <Input
                  value={filters.search}
                  onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                  placeholder="Omschrijving, klant..."
                  className="bg-gray-50 border-gray-200 pl-8"
                  data-testid="expense-search"
                />
              </div>
            </div>
            <div>
              <Label className="text-gray-700 text-xs">Bedrijf</Label>
              <Select value={filters.client_name} onValueChange={(v) => setFilters({ ...filters, client_name: v })}>
                <SelectTrigger className="bg-gray-50 border-gray-200" data-testid="filter-client"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle bedrijven</SelectItem>
                  {clientOptions.map(name => <SelectItem key={name} value={name}>{name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-gray-700 text-xs">Categorie</Label>
              <Select value={filters.category} onValueChange={(v) => setFilters({ ...filters, category: v })}>
                <SelectTrigger className="bg-gray-50 border-gray-200" data-testid="filter-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle categorieën</SelectItem>
                  {Object.entries(CATEGORY_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {user?.is_admin && (
              <div>
                <Label className="text-gray-700 text-xs">Medewerker</Label>
                <Select value={filters.user_id} onValueChange={(v) => setFilters({ ...filters, user_id: v })}>
                  <SelectTrigger className="bg-gray-50 border-gray-200" data-testid="filter-user"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Alle medewerkers</SelectItem>
                    {users.map(u => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label className="text-gray-700 text-xs">Van datum</Label>
              <Input type="date" value={filters.start_date} onChange={(e) => setFilters({ ...filters, start_date: e.target.value })} className="bg-gray-50 border-gray-200" />
            </div>
            <div>
              <Label className="text-gray-700 text-xs">Tot datum</Label>
              <Input type="date" value={filters.end_date} onChange={(e) => setFilters({ ...filters, end_date: e.target.value })} className="bg-gray-50 border-gray-200" />
            </div>
          </div>
          <div className="mt-3 flex justify-between items-center">
            <p className="text-xs text-gray-500">{totalCount} resultaten · {fmtEuro(totalAmount)}</p>
            <Button variant="outline" size="sm" onClick={clearFilters} className="border-gray-300" data-testid="clear-filters-btn">
              <X className="w-3 h-3 mr-1" /> Filters wissen
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* List */}
      {expenses.length === 0 ? (
        <Card className="bg-white shadow-sm border-gray-200">
          <CardContent className="py-12 text-center">
            <Receipt className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">Geen uitgaven gevonden met deze filters</p>
          </CardContent>
        </Card>
      ) : (
        <Card className="bg-white shadow-sm border-gray-200">
          {user?.is_admin && selected.size > 0 && (
            <div className="flex flex-wrap items-center gap-3 px-4 py-2 bg-[#1a5276]/5 border-b border-[#1a5276]/20 text-sm" data-testid="bulk-move-bar">
              <span className="font-semibold text-[#1a5276]" data-testid="bulk-selected-count">{selected.size} geselecteerd · {fmtEuro(expenses.filter((e) => selected.has(e.id)).reduce((a, e) => a + Number(e.amount || 0), 0))}</span>
              <span className="text-gray-600">Verplaatsen naar:</span>
              <Button size="sm" variant="outline" disabled={bulkMoving} onClick={() => handleBulkMove('purchase')} className="border-[#1a5276] text-[#1a5276] h-8" data-testid="bulk-move-purchase-btn"><ArrowRightLeft className="w-3.5 h-3.5 mr-1" /> Inkoopfacturen</Button>
              <Button size="sm" variant="outline" disabled={bulkMoving} onClick={() => handleBulkMove('external')} className="border-[#1a5276] text-[#1a5276] h-8" data-testid="bulk-move-external-btn"><ArrowRightLeft className="w-3.5 h-3.5 mr-1" /> Facturen van derden</Button>
              <button type="button" className="text-xs text-gray-500 underline ml-auto" onClick={() => setSelected(new Set())} data-testid="bulk-clear-btn">Selectie wissen</button>
            </div>
          )}
          <CardContent className="p-0 overflow-x-auto">
            <table className="w-full text-sm" data-testid="expenses-table">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  {user?.is_admin && <th className="py-3 px-3 w-8"><input type="checkbox" checked={allSelected} onChange={toggleAll} className="w-4 h-4 cursor-pointer" title="Alles selecteren" data-testid="select-all-expenses" /></th>}
                  <th className="text-left py-3 px-4 text-gray-600 font-semibold">Datum</th>
                  <th className="text-left py-3 px-4 text-gray-600 font-semibold">Bedrijf / Klant</th>
                  <th className="text-left py-3 px-4 text-gray-600 font-semibold">Adres</th>
                  <th className="text-left py-3 px-4 text-gray-600 font-semibold">Omschrijving</th>
                  <th className="text-left py-3 px-4 text-gray-600 font-semibold">Categorie</th>
                  {user?.is_admin && <th className="text-left py-3 px-4 text-gray-600 font-semibold">Medewerker</th>}
                  <th className="text-right py-3 px-4 text-gray-600 font-semibold">Excl. btw</th>
                  <th className="text-right py-3 px-4 text-gray-600 font-semibold">Btw</th>
                  <th className="text-right py-3 px-4 text-gray-600 font-semibold">Incl. btw</th>
                  <th className="text-center py-3 px-4 text-gray-600 font-semibold">Bon</th>
                  <th className="text-right py-3 px-4 text-gray-600 font-semibold">Acties</th>
                </tr>
              </thead>
              <tbody>
                {expenses.map(exp => (
                  <tr key={exp.id} className={`border-b border-gray-100 hover:bg-gray-50 ${selected.has(exp.id) ? 'bg-[#1a5276]/5' : ''}`} data-testid={`expense-row-${exp.id}`}>
                    {user?.is_admin && <td className="py-2 px-3"><input type="checkbox" checked={selected.has(exp.id)} onChange={() => toggleSelect(exp.id)} className="w-4 h-4 cursor-pointer" data-testid={`select-expense-${exp.id}`} /></td>}
                    <td className="py-2 px-4 text-gray-700 whitespace-nowrap">{formatDateNL(exp.date)}</td>
                    <td className="py-2 px-4 text-gray-700 font-medium">{exp.client_name || exp.project_name}</td>
                    <td className="py-2 px-4 text-gray-500 text-xs">{exp.address || '-'}</td>
                    <td className="py-2 px-4 text-gray-700">{exp.description}</td>
                    <td className="py-2 px-4">
                      {readOnly ? (
                        <span className="text-xs px-2 py-1 rounded border bg-gray-100 border-gray-200 text-gray-800" data-testid={`category-label-${exp.id}`}>{CATEGORY_LABELS[exp.category] || CATEGORY_LABELS.other}</span>
                      ) : (
                      <select
                        value={exp.category || 'other'}
                        onChange={(e) => changeCategory(exp, e.target.value)}
                        className={`text-xs px-2 py-1 rounded border cursor-pointer max-w-[190px] ${exp.category_by_user ? 'bg-emerald-50 border-emerald-300 text-emerald-900' : exp.category_by_rule ? 'bg-sky-50 border-sky-200 text-sky-900' : 'bg-gray-100 border-gray-200 text-gray-800'}`}
                        title={exp.category_by_user ? 'Door jou gekozen' : exp.category_by_rule ? 'Geleerd van eerdere keuze voor deze leverancier' : exp.category_by_ai ? 'Door AI ingedeeld — klik om aan te passen' : 'Klik om categorie aan te passen'}
                        data-testid={`category-select-${exp.id}`}
                      >
                        {Object.entries(CATEGORY_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                      </select>
                      )}
                    </td>
                    {user?.is_admin && <td className="py-2 px-4 text-gray-500 text-xs">{exp.created_by_name || '-'}</td>}
                    {(() => { const b = btwSplit(exp); return (<>
                      <td className="py-2 px-4 text-right text-gray-700" data-testid={`expense-excl-${exp.id}`}>{fmtEuro(b.excl)}</td>
                      <td className="py-2 px-4 text-right text-gray-700 whitespace-nowrap" data-testid={`expense-btw-${exp.id}`} title={exp.btw_by_user ? 'Btw door jou gecorrigeerd' : 'Klik op het percentage om te corrigeren'}>
                        {fmtEuro(b.btw)}{' '}
                        {readOnly ? <span className="text-[11px] text-gray-500">({b.rate}%)</span> : (
                        <select value={[0, 9, 21].includes(b.rate) ? b.rate : 21} onChange={(e) => changeBtw(exp, Number(e.target.value))} className={`text-[11px] rounded border px-1 py-0.5 cursor-pointer ${exp.btw_by_user ? 'bg-emerald-50 border-emerald-300 text-emerald-900' : 'bg-gray-50 border-gray-200 text-gray-600'}`} data-testid={`btw-select-${exp.id}`}>
                          <option value={0}>0%</option><option value={9}>9%</option><option value={21}>21%</option>
                        </select>
                        )}
                      </td>
                      <td className="py-2 px-4 text-right font-bold text-[#3da5a5]" data-testid={`expense-incl-${exp.id}`}>
                        {editingAmountId === exp.id ? (
                          <input autoFocus type="number" step="0.01" min="0.01" value={amountDraft} onChange={(e) => setAmountDraft(e.target.value)}
                            onBlur={() => saveAmount(exp)} onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); if (e.key === 'Escape') setEditingAmountId(null); }}
                            className="w-24 text-right border border-[#3da5a5] rounded px-1 py-0.5 text-sm font-semibold text-gray-800" data-testid={`amount-input-${exp.id}`} />
                        ) : (!readOnly && (user?.is_admin || exp.created_by === user?.id)) ? (
                          <button type="button" onClick={() => startAmountEdit(exp)} className={`hover:underline decoration-dotted ${exp.amount_by_user ? 'text-emerald-700' : ''}`} title={exp.amount_by_user ? 'Bedrag door jou gecorrigeerd — klik om aan te passen' : 'Klik om het bedrag aan te passen'} data-testid={`amount-edit-${exp.id}`}>{fmtEuro(b.incl)}</button>
                        ) : fmtEuro(b.incl)}
                      </td>
                    </>); })()}
                    <td className="py-2 px-4 text-center">
                      {(exp.has_receipt || exp.receipt_url) ? (
                        <button type="button" onClick={() => openReceipt(exp)} className="inline-flex items-center text-blue-600 hover:text-blue-700" title="Bekijk bon" data-testid={`view-receipt-${exp.id}`}>
                          <FileText className="w-4 h-4" />
                        </button>
                      ) : (
                        <span className="text-gray-300 text-xs">—</span>
                      )}
                    </td>
                    <td className="py-2 px-4 text-right">
                      <div className="flex justify-end gap-1">
                        {user?.is_admin && (
                          <label className="inline-flex items-center gap-1 text-[#1a5276] h-8 px-1" title="Verplaats deze uitgave naar Inkoopfacturen of Facturen van derden">
                            <ArrowRightLeft className="w-4 h-4" />
                            <select value="" onChange={(e) => handleMove(exp, e.target.value)} className="text-[11px] rounded border border-gray-200 bg-white px-1 py-0.5 cursor-pointer text-gray-700" data-testid={`move-expense-${exp.id}`}>
                              <option value="">Verplaatsen naar…</option>
                              {Object.entries(MOVE_TARGETS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                            </select>
                          </label>
                        )}
                        {(!readOnly && (user?.is_admin || exp.created_by === user?.id)) && (
                          <>
                            <Button variant="ghost" size="sm" onClick={() => handleEdit(exp)} className="text-gray-600 h-8 px-2" data-testid={`edit-expense-${exp.id}`}><Edit className="w-4 h-4" /></Button>
                            <Button variant="ghost" size="sm" onClick={() => handleDelete(exp.id)} className="text-red-500 h-8 px-2"><Trash2 className="w-4 h-4" /></Button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {/* Dialog */}
      <Dialog open={open} onOpenChange={(o) => { if (!o) resetForm(); else setOpen(true); }}>
        <DialogContent className="bg-white shadow-sm border-gray-200 text-gray-800 max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-2xl">{editingId ? 'Uitgave bewerken' : 'Nieuwe Uitgave'}</DialogTitle>
            <DialogDescription className="sr-only">Vul de gegevens van de uitgave in</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label className="text-gray-800 mb-2 block">Bedrijf *</Label>
              <Select
                value={selectedClientId}
                onValueChange={(value) => {
                  const client = clients.find(c => c.id === value);
                  if (client) {
                    setSelectedClientId(value);
                    setFormData({ ...formData, client_name: client.name, address: '' });
                  }
                }}
              >
                <SelectTrigger className="bg-gray-50 border-gray-200 text-gray-800" data-testid="dialog-client-select">
                  <SelectValue placeholder="Selecteer bedrijf" />
                </SelectTrigger>
                <SelectContent>
                  {clients.map(client => (
                    <SelectItem key={client.id} value={client.id}>{client.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {selectedClientId && (() => {
              const client = clients.find(c => c.id === selectedClientId);
              const addresses = client?.addresses || [];
              if (addresses.length === 0) return null;
              return (
                <div>
                  <Label className="text-gray-800 mb-2 block">Klant (Adres) *</Label>
                  <Select
                    value={formData.address}
                    onValueChange={(value) => setFormData({ ...formData, address: value })}
                  >
                    <SelectTrigger className="bg-gray-50 border-gray-200 text-gray-800" data-testid="dialog-address-select">
                      <SelectValue placeholder="Selecteer klant/adres" />
                    </SelectTrigger>
                    <SelectContent>
                      {addresses.map(addr => (
                        <SelectItem key={addr.id} value={addr.address}>
                          {addr.label ? `${addr.label} - ${addr.address}` : addr.address}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              );
            })()}

            <div>
              <Label className="text-gray-800 mb-2 block">Omschrijving *</Label>
              <Input
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                required
                placeholder="Bijv: cement, parkeerkosten OLVG"
                className="bg-gray-50 border-gray-200 text-gray-800"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-gray-800 mb-2 block">Bedrag (€) *</Label>
                <Input
                  type="number" step="0.01"
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  required
                  className="bg-gray-50 border-gray-200 text-gray-800"
                />
              </div>
              <div>
                <Label className="text-gray-800 mb-2 block">Datum *</Label>
                <Input
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  required
                  className="bg-gray-50 border-gray-200 text-gray-800"
                />
              </div>
            </div>

            <div>
              <Label className="text-gray-800 mb-2 block">Categorie *</Label>
              <Select value={formData.category} onValueChange={(value) => setFormData({ ...formData, category: value })}>
                <SelectTrigger className="bg-gray-50 border-gray-200 text-gray-800"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(CATEGORY_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-gray-800 mb-2 block">Bon (foto/PDF) {editingId && '— laat leeg om te behouden'}</Label>
              <Input
                type="file" accept=".pdf,.jpg,.jpeg,.png,.webp"
                onChange={(e) => setSelectedReceipt(e.target.files[0])}
                className="bg-gray-50 border-gray-200 text-gray-800"
              />
              {selectedReceipt && (
                <p className="text-xs text-[#2d8585] mt-1" data-testid="receipt-attached-hint">✓ Bon bijgevoegd: {selectedReceipt.name}</p>
              )}
            </div>

            <Button type="submit" className="w-full bg-[#3da5a5] hover:bg-[#2d8585] py-5" data-testid="save-expense-btn">
              {editingId ? 'Bijwerken' : 'Toevoegen'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Expenses;
